import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit, Trash2, Package } from "lucide-react";
import { productService, Product } from "../../services/product.service";
import { showToast } from "../../lib/toast";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";

export function ViewProductPage() {
  console.log("[ViewProductPage] Component rendering");
  const { id } = useParams<{ id: string }>();
  console.log("[ViewProductPage] Product ID:", id);
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    console.log("[ViewProductPage] useEffect triggered, id:", id);
    if (id) {
      loadProduct();
    } else {
      setError("No product ID provided");
      setLoading(false);
    }
  }, [id]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await productService.getProduct(id!);
      setProduct(data);
    } catch (err: any) {
      console.error("Failed to load product:", err);
      const message = err.response?.data?.message || "Failed to load product";
      setError(message);
      showToast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await productService.deleteProduct(id!);
      showToast.success("Product deleted successfully");
      navigate("/products");
    } catch (err: any) {
      showToast.error(
        err.response?.data?.message || "Failed to delete product",
      );
    } finally {
      setIsDeleting(false);
      setDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate("/products")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Products
        </button>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-8 rounded-lg text-center">
          <p className="font-medium">{error}</p>
          <button
            onClick={loadProduct}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Product not found</div>
      </div>
    );
  }

  const getScheduleColor = (schedule: string) => {
    switch (schedule) {
      case "SCHEDULE_H1":
        return "bg-red-100 text-red-800";
      case "SCHEDULE_H":
        return "bg-orange-100 text-orange-800";
      case "SCHEDULE_X":
        return "bg-purple-100 text-purple-800";
      case "SCHEDULE_C":
      case "SCHEDULE_C1":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/products")}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Products
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {product.name}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                SKU: {product.sku}
                {product.barcode && ` | Barcode: ${product.barcode}`}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/products/${id}/edit`)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Edit className="h-4 w-4" />
              Edit
            </button>
            <button
              onClick={() => setDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Product Name</span>
              <span className="font-medium">{product.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">SKU</span>
              <span className="font-medium font-mono">{product.sku}</span>
            </div>
            {product.barcode && (
              <div className="flex justify-between">
                <span className="text-gray-500">Barcode</span>
                <span className="font-medium font-mono">{product.barcode}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Category</span>
              <span className="font-medium">
                {product.category || "-"}
                {product.subcategory && (
                  <span className="text-gray-500 text-sm ml-1">
                    ({product.subcategory})
                  </span>
                )}
              </span>
            </div>
            {product.manufacturer && (
              <div className="flex justify-between">
                <span className="text-gray-500">Manufacturer</span>
                <span className="font-medium">{product.manufacturer}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500">Unit</span>
              <span className="font-medium">{product.unit}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Status</span>
              <span
                className={`px-2 py-1 text-xs rounded-full ${
                  product.status === "ACTIVE"
                    ? "bg-green-100 text-green-800"
                    : "bg-red-100 text-red-800"
                }`}
              >
                {product.status}
              </span>
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Pricing</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Purchase Price</span>
              <span className="font-medium">
                ₹{Number(product.purchasePrice ?? 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Selling Price</span>
              <span className="font-medium text-green-600">
                ₹{Number(product.sellingPrice ?? 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">MRP</span>
              <span className="font-medium">
                ₹{Number(product.mrp ?? 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">GST</span>
              <span className="font-medium">{product.gstPercent ?? 0}%</span>
            </div>
            {product.hsnCode && (
              <div className="flex justify-between">
                <span className="text-gray-500">HSN Code</span>
                <span className="font-medium">{product.hsnCode}</span>
              </div>
            )}
          </div>
        </div>

        {/* Drug Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Drug Information</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-500">Schedule Type</span>
              <span
                className={`px-2 py-1 text-xs rounded-full ${getScheduleColor(product.scheduleType || "OTC")}`}
              >
                {product.scheduleType?.replace("_", " ") || "OTC"}
              </span>
            </div>
            {product.composition && (
              <div>
                <span className="text-gray-500 block mb-1">Composition</span>
                <span className="font-medium text-sm">
                  {product.composition}
                </span>
              </div>
            )}
            {product.strength && (
              <div className="flex justify-between">
                <span className="text-gray-500">Strength</span>
                <span className="font-medium">{product.strength}</span>
              </div>
            )}
          </div>
        </div>

        {/* Inventory */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Inventory</h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Current Stock</span>
              <span
                className={`font-medium ${
                  (product.totalStock || 0) > 10
                    ? "text-green-600"
                    : (product.totalStock || 0) > 0
                      ? "text-yellow-600"
                      : "text-red-600"
                }`}
              >
                {product.totalStock || 0} {product.unit}
              </span>
            </div>
            {product.minStockLevel !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-500">Min Stock Level</span>
                <span className="font-medium">
                  {product.minStockLevel} {product.unit}
                </span>
              </div>
            )}
            {product.maxStockLevel !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-500">Max Stock Level</span>
                <span className="font-medium">
                  {product.maxStockLevel} {product.unit}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Description */}
        {product.description && (
          <div className="bg-white rounded-lg shadow p-6 lg:col-span-2">
            <h2 className="text-lg font-semibold mb-4">Description</h2>
            <p className="text-gray-700 whitespace-pre-wrap">
              {product.description}
            </p>
          </div>
        )}

        {/* Batches */}
        {product.batches && product.batches.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 lg:col-span-2">
            <h2 className="text-lg font-semibold mb-4">
              Batches ({product.batches.length})
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Batch #
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Expiry
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Quantity
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Cost Price
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      MRP
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {product.batches.map((batch) => {
                    const isExpired = new Date(batch.expiryDate) < new Date();
                    const isExpiringSoon =
                      !isExpired &&
                      new Date(batch.expiryDate) <
                        new Date(Date.now() + 90 * 24 * 60 * 60 * 1000);
                    return (
                      <tr
                        key={batch.id}
                        className={
                          isExpired
                            ? "bg-red-50"
                            : isExpiringSoon
                              ? "bg-yellow-50"
                              : ""
                        }
                      >
                        <td className="px-4 py-3 font-mono text-sm">
                          {batch.batchNumber}
                        </td>
                        <td
                          className={`px-4 py-3 text-sm ${isExpired ? "text-red-600 font-medium" : isExpiringSoon ? "text-yellow-600" : ""}`}
                        >
                          {new Date(batch.expiryDate).toLocaleDateString()}
                          {isExpired && " (Expired)"}
                          {isExpiringSoon && " (Expiring Soon)"}
                        </td>
                        <td className="px-4 py-3 text-sm">{batch.quantity}</td>
                        <td className="px-4 py-3 text-sm">
                          ₹{Number(batch.costPrice ?? 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          ₹{Number(batch.mrp ?? 0).toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Product"
        message={`Are you sure you want to delete "${product.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isDeleting}
        variant="danger"
      />
    </div>
  );
}

export default ViewProductPage;
